import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { APP_CONFIG } from '@core-app/app.config';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { getMessaging, getToken, Messaging, onMessage } from 'firebase/messaging';
import { Subscription } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    private swPush = inject(SwPush);
    private http = inject(HttpClient);
    private platformId = inject(PLATFORM_ID);

    private readonly VAPID_PUBLIC_KEY = '2MDO6Ksmvxzw6zhF09gjtxYdIExK58gHnZTl73EqaUc';
    private messaging: Messaging | null = null;
    private firebaseApp: FirebaseApp | null = null;
    private notificationSubscription?: Subscription;

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.initFirebase();
        }
    }

    requestPermission(): void {
        if (!isPlatformBrowser(this.platformId) || !this.messaging) return;

        getToken(this.messaging, {
            vapidKey: this.VAPID_PUBLIC_KEY,
        })
            .then(token => {
                if (token) {
                    console.log('Push token:', token);
                }
            })
            .catch(err => {
                console.error('FCM token request error:', err);
            });
    }

    receiveMessage(): void {
        if (!isPlatformBrowser(this.platformId) || !this.messaging) return;

        onMessage(this.messaging, payload => {
            console.log('Message received:', payload);
            this.showCustomNotification(payload);
        });
    }

    showCustomNotification(message: any): void {
        if (!isPlatformBrowser(this.platformId)) return;

        const { title, body } = message.notification || {};
        if ('Notification' in window && title && body) {
            new Notification(title, {
                body,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
            });
        }
    }

    subscribeToNotifications(): void {
        if (!isPlatformBrowser(this.platformId)) return;

        this.swPush
            .requestSubscription({
                serverPublicKey: this.urlBase64ToUint8Array(this.VAPID_PUBLIC_KEY) as unknown as string,
            })
            .then(subscription => {
                this.sendSubscriptionToTheServer(subscription);
            })
            .catch(err => console.error('Could not subscribe to notifications', err));
    }

    private initFirebase(): void {
        // Avoid re-initialisation
        const LOCAL_APP_CONFIG = APP_CONFIG as any;
        if (this.firebaseApp || !LOCAL_APP_CONFIG.firebaseNotifications) return;

        this.firebaseApp = initializeApp(LOCAL_APP_CONFIG.firebaseNotifications);

        this.messaging = getMessaging(this.firebaseApp);
    }

    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        if (!isPlatformBrowser(this.platformId)) return new Uint8Array();

        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }

        return outputArray;
    }

    private sendSubscriptionToTheServer(subscription: PushSubscription): void {
        this.notificationSubscription = this.http.post('/pwa-subscribe', subscription).subscribe();
    }
}
